
import React, { useState } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dumbbell, Search } from 'lucide-react';
import { useWorkoutTypes } from '@/hooks/useWorkoutTypes';
import WorkoutTypeForm from '@/components/workout-types/WorkoutTypeForm';
import WorkoutTypeCard from '@/components/workout-types/WorkoutTypeCard';
import WorkoutTypeDeleteDialog from '@/components/workout-types/WorkoutTypeDeleteDialog';
import { WorkoutType } from '@/models/WorkoutType';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const WorkoutTypes = () => {
  const { currentUserRole } = useAuth();
  const { t } = useTranslation();
  const [isAddWorkoutTypeOpen, setIsAddWorkoutTypeOpen] = useState(false);
  const [editingWorkoutType, setEditingWorkoutType] =
    useState<WorkoutType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<WorkoutType | null>(null);

  const {
    workoutTypes,
    loading,
    createWorkoutType,
    updateWorkoutType,
    deleteWorkoutType,
  } = useWorkoutTypes();

  // Check if user can manage workout types (admin or manager)
  const canManageWorkoutTypes =
    currentUserRole === 'admin' || currentUserRole === 'manager';

  // Filter workout types based on search term
  const filteredWorkoutTypes = workoutTypes.filter((workoutType) =>
    workoutType.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditWorkoutType = (id: string) => {
    if (canManageWorkoutTypes) {
      const workoutType = workoutTypes.find((wt) => wt.id === id);
      if (workoutType) {
        setEditingWorkoutType(workoutType);
      }
    }
  };

  const handleDeleteWorkoutType = (id: string) => {
    if (canManageWorkoutTypes) {
      const workoutType = workoutTypes.find((wt) => wt.id === id);
      if (workoutType) {
        setDeleteDialog(workoutType);
      }
    }
  };

  const confirmDeleteWorkoutType = async () => {
    if (deleteDialog) {
      await deleteWorkoutType(deleteDialog.id);
      setDeleteDialog(null);
    }
  };

  const handleCloseEditModal = () => {
    setEditingWorkoutType(null);
  };

  const handleCreateWorkoutType = async (data: unknown) => {
    await createWorkoutType(data);
    setIsAddWorkoutTypeOpen(false);
  };

  const handleUpdateWorkoutType = async (data: unknown) => {
    if (editingWorkoutType) {
      await updateWorkoutType(editingWorkoutType.id, data);
      setEditingWorkoutType(null);
    }
  };

  return (
    <MainLayout>
      <div className="py-4 md:py-6 space-y-4 md:space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4">
          <h1 className="text-2xl md:text-3xl font-bold greek-text">
            {t('workoutTypes.title')}
          </h1>
          {canManageWorkoutTypes && (
            <Button
              onClick={() => setIsAddWorkoutTypeOpen(true)}
              className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm"
              size="sm"
            >
              <Dumbbell className="h-4 w-4 md:h-5 md:w-5" />
              {t('workoutTypes.addWorkoutType')}
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3 md:gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 md:left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-3 w-3 md:h-4 md:w-4" />
            <Input
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 md:pl-10 text-xs md:text-sm h-8 md:h-10"
            />
          </div>
        </div>

        {!canManageWorkoutTypes && (
          <div className="mb-4 p-3 md:p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-xs md:text-sm">
              {t('workoutTypes.cannotManage')}
            </p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">{t('common.loading')}</div>
        ) : filteredWorkoutTypes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchTerm
              ? `No workout types found matching "${searchTerm}".`
              : t('workoutTypes.noWorkoutTypes')}
            {!searchTerm && !canManageWorkoutTypes && (
              <p className="mt-2">{t('workoutTypes.createFirst')}</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredWorkoutTypes.map((workoutType) => (
              <WorkoutTypeCard
                key={workoutType.id}
                workoutType={workoutType}
                onEdit={handleEditWorkoutType}
                onDelete={handleDeleteWorkoutType}
                canManage={canManageWorkoutTypes}
              />
            ))}
          </div>
        )}
      </div>

      {canManageWorkoutTypes && (
        <WorkoutTypeForm
          open={isAddWorkoutTypeOpen}
          onOpenChange={setIsAddWorkoutTypeOpen}
          onSubmit={handleCreateWorkoutType}
        />
      )}

      {editingWorkoutType && canManageWorkoutTypes && (
        <WorkoutTypeForm
          open={!!editingWorkoutType}
          onOpenChange={handleCloseEditModal}
          initialData={editingWorkoutType}
          onSubmit={handleUpdateWorkoutType}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <WorkoutTypeDeleteDialog
        workoutType={deleteDialog}
        open={deleteDialog !== null}
        onOpenChange={() => setDeleteDialog(null)}
        onConfirm={confirmDeleteWorkoutType}
      />
    </MainLayout>
  );
};

export default WorkoutTypes;
